import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

const TODAY = new Date().toISOString().split('T')[0];

function MyClientVisit() {
  const { user } = useAuth();
  const isManager = user?.is_team_lead;
  const isHrAdmin = ['hr_admin', 'hr_head', 'super_admin', 'ceo', 'cfo', 'cto'].includes(user?.role);
  const [activeTab, setActiveTab] = useState('My Visits');
  const [myRequests, setMyRequests] = useState([]);
  const [teamRequests, setTeamRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyModal, setApplyModal] = useState(false);
  const [form, setForm] = useState({ visit_date: '', client_name: '', client_location: '', purpose: '', expected_duration: '' });
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [saving, setSaving] = useState(false);

  const loadMine = useCallback(async () => {
    setLoading(true);
    try {
      const r = await hrms.getClientVisitRequests();
      const d = r.data;
      setMyRequests(Array.isArray(d) ? d : []);
    } catch { setMyRequests([]); } finally { setLoading(false); }
  }, []);

  const loadTeam = useCallback(async () => {
    try {
      const r = await hrms.getTeamClientVisitRequests();
      const d = r.data;
      setTeamRequests(Array.isArray(d) ? d : []);
    } catch { setTeamRequests([]); }
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const r = await hrms.getClientVisitRequests();
      setAllRequests(Array.isArray(r.data) ? r.data : []);
    } catch { setAllRequests([]); }
  }, []);

  useEffect(() => { loadMine(); }, [loadMine]);
  useEffect(() => { if (isManager && activeTab === 'Team Requests') loadTeam(); }, [isManager, activeTab, loadTeam]);
  useEffect(() => { if (isHrAdmin && activeTab === 'All Requests') loadAll(); }, [isHrAdmin, activeTab, loadAll]);

  const handleSubmit = async () => {
    if (!form.visit_date || !form.client_name || !form.purpose) {
      alert('Visit date, client name, and purpose are required.'); return;
    }
    setSaving(true);
    try {
      await hrms.createClientVisitRequest(form);
      setApplyModal(false);
      setForm({ visit_date: '', client_name: '', client_location: '', purpose: '', expected_duration: '' });
      loadMine();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to submit request.');
    } finally { setSaving(false); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this client visit request?')) return;
    try { await hrms.cancelClientVisitRequest(id); loadMine(); } catch { alert('Failed.'); }
  };

  const handleApprove = async (id) => {
    try { await hrms.approveClientVisitRequest(id); loadTeam(); } catch { alert('Failed.'); }
  };

  const handleReject = async () => {
    setSaving(true);
    try {
      await hrms.rejectClientVisitRequest(rejectModal, rejectReason);
      setRejectModal(null); setRejectReason(''); loadTeam();
    } catch { alert('Failed.'); } finally { setSaving(false); }
  };

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  const renderTable = (rows, isTeam) => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ background: '#F8FAFC' }}>
            {[...(isTeam ? ['Employee'] : []), 'Visit Date', 'Client', 'Location', 'Purpose', 'Duration', 'Status', 'Actions'].map((h) => (
              <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: theme.textMuted, borderBottom: `2px solid ${theme.border}`, fontSize: '0.78rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading...</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>No requests found</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}` }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#F8FAFC')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              {isTeam && <td style={{ padding: '0.75rem 1rem', fontWeight: 500 }}>{r.employee_name}</td>}
              <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>{r.visit_date ? new Date(r.visit_date).toLocaleDateString('en-IN') : '—'}</td>
              <td style={{ padding: '0.75rem 1rem', fontWeight: 500 }}>{r.client_name}</td>
              <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.client_location || '—'}</td>
              <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.purpose || '—'}</td>
              <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', color: theme.textMuted }}>{r.expected_duration || '—'}</td>
              <td style={{ padding: '0.75rem 1rem' }}><Badge status={r.status} byName={r.approved_by_name || r.pending_approver_name} /></td>
              <td style={{ padding: '0.75rem 1rem' }}>
                {!isTeam && r.status === 'pending' && (
                  <Button size="sm" variant="danger" onClick={() => handleCancel(r.id)}>Cancel</Button>
                )}
                {isTeam && r.status === 'pending' && (
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <Button size="sm" variant="success" onClick={() => handleApprove(r.id)}>Approve</Button>
                    <Button size="sm" variant="danger" onClick={() => { setRejectModal(r.id); setRejectReason(''); }}>Reject</Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const tabList = [
    'My Visits',
    ...(isManager ? ['Team Requests'] : []),
    ...(isHrAdmin ? ['All Requests'] : []),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Client Visit Requests</h1>
        <Button variant="primary" onClick={() => setApplyModal(true)}>+ New Request</Button>
      </div>

      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: `2px solid ${theme.border}` }}>
        {tabList.map((t) => (
          <button key={t} onClick={() => setActiveTab(t)} style={{ padding: '0.625rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === t ? 700 : 400, color: activeTab === t ? theme.primary : theme.textMuted, borderBottom: activeTab === t ? `2px solid ${theme.primary}` : '2px solid transparent', marginBottom: '-2px', fontSize: '0.875rem' }}>
            {t}
          </button>
        ))}
      </div>

      {activeTab === 'My Visits' && (
        <Card padding="0">{renderTable(myRequests, false)}</Card>
      )}

      {activeTab === 'Team Requests' && isManager && (
        <Card padding="0">{renderTable(teamRequests, true)}</Card>
      )}

      {activeTab === 'All Requests' && isHrAdmin && (
        <Card padding="0">{renderTable(allRequests, true)}</Card>
      )}

      {/* Apply Modal */}
      <Modal isOpen={applyModal} onClose={() => setApplyModal(false)} title="New Client Visit Request"
        footer={<><Button variant="outlineGray" onClick={() => setApplyModal(false)}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleSubmit}>Submit</Button></>}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={lbl}>Visit Date *</label>
            <input style={inp} type="date" min={TODAY} value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} />
          </div>
          <div>
            <label style={lbl}>Expected Duration</label>
            <input style={inp} type="text" placeholder="e.g. 2 hours, Half Day" value={form.expected_duration} onChange={(e) => setForm({ ...form, expected_duration: e.target.value })} />
          </div>
        </div>
        <label style={lbl}>Client Name *</label>
        <input style={inp} type="text" placeholder="Client / Company name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
        <label style={lbl}>Client Location</label>
        <input style={inp} type="text" placeholder="Address or city" value={form.client_location} onChange={(e) => setForm({ ...form, client_location: e.target.value })} />
        <label style={lbl}>Purpose *</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '80px' }} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Purpose of visit..." />
      </Modal>

      {/* Reject Modal */}
      <Modal isOpen={!!rejectModal} onClose={() => setRejectModal(null)} title="Reject Client Visit"
        footer={<><Button variant="outlineGray" onClick={() => setRejectModal(null)}>Cancel</Button><Button variant="danger" loading={saving} onClick={handleReject}>Reject</Button></>}>
        <label style={lbl}>Reason *</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '80px' }} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason for rejection..." />
      </Modal>
    </div>
  );
}

export default MyClientVisit;
